<script setup>
import AgentEmotion from "@/components/shared/AgentEmotion.vue";

defineProps({
  tag: { type: String, default: "AI Operations Console" },
  titleLine: { type: String, required: true },
  titleEmphasis: { type: String, required: true },
  description: { type: String, required: true },
  features: { type: Array, default: () => [] },
  emotion: { type: String, default: "greeting" },
});
</script>

<template>
  <aside class="auth-hero" aria-hidden="true">
    <span class="auth-hero__orb auth-hero__orb--1"></span>
    <span class="auth-hero__orb auth-hero__orb--2"></span>
    <span class="auth-hero__grid"></span>

    <div class="auth-hero__medallion">
      <span class="auth-hero__ring"></span>
      <span class="auth-hero__ring auth-hero__ring--2"></span>
      <span class="auth-hero__glow"></span>
      <span class="auth-hero__dot"></span>
      <AgentEmotion :emotion="emotion" size="96" class="auth-hero__face" />
    </div>

    <div class="auth-hero__body">
      <span class="auth-hero__tag">{{ tag }}</span>
      <h2 class="auth-hero__headline">{{ titleLine }} <em>{{ titleEmphasis }}</em></h2>
      <p class="auth-hero__desc">{{ description }}</p>
      <ul v-if="features.length" class="auth-hero__list">
        <li v-for="feature in features" :key="feature">
          <span class="auth-hero__tick"><i class="bi bi-check-lg"></i></span> {{ feature }}
        </li>
      </ul>
    </div>
  </aside>
</template>

<style scoped>
.auth-hero {
  position: relative;
  overflow: hidden;
  color: #eaf0ff;
  background:
    radial-gradient(120% 90% at 15% 10%, #2c3f74 0%, transparent 55%),
    radial-gradient(130% 100% at 90% 90%, #1a2647 0%, transparent 60%),
    linear-gradient(155deg, var(--primary-800, #1d2d51) 0%, var(--primary-900, #151f3a) 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 56px;
}

.auth-hero__orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(2px);
  opacity: 0.5;
  pointer-events: none;
}

.auth-hero__orb--1 {
  width: 340px;
  height: 340px;
  top: -80px;
  right: -60px;
  background: radial-gradient(circle at 35% 35%, rgba(97, 116, 169, 0.55), transparent 70%);
  animation: auth-hero-drift-1 16s ease-in-out infinite;
}

.auth-hero__orb--2 {
  width: 260px;
  height: 260px;
  bottom: -40px;
  left: -50px;
  background: radial-gradient(circle at 50% 50%, rgba(252, 140, 2, 0.22), transparent 70%);
  animation: auth-hero-drift-2 20s ease-in-out infinite;
}

.auth-hero__grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 46px 46px;
  -webkit-mask-image: radial-gradient(circle at 50% 40%, #000 0%, transparent 78%);
  mask-image: radial-gradient(circle at 50% 40%, #000 0%, transparent 78%);
  pointer-events: none;
}

@keyframes auth-hero-drift-1 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-26px, 30px); }
}

@keyframes auth-hero-drift-2 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(28px, -22px); }
}

.auth-hero__medallion {
  position: relative;
  z-index: 2;
  align-self: center;
  margin: 0 auto 34px;
  width: 168px;
  height: 168px;
  display: grid;
  place-items: center;
}

.auth-hero__ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.14);
}

.auth-hero__ring--2 {
  inset: 18px;
  border-color: rgba(252, 140, 2, 0.28);
  animation: auth-hero-pulse 4s ease-in-out infinite;
}

.auth-hero__glow {
  position: absolute;
  inset: 26px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(252, 140, 2, 0.22), transparent 70%);
}

.auth-hero__face {
  position: relative;
  z-index: 1;
  filter: drop-shadow(0 8px 20px rgba(0, 0, 0, 0.4));
}

.auth-hero__dot {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 9px;
  height: 9px;
  margin: -4.5px;
  border-radius: 50%;
  background: var(--brand-orange, #fc8c02);
  box-shadow: 0 0 12px var(--brand-orange, #fc8c02);
  transform-origin: -75px 0;
  animation: auth-hero-orbit 7s linear infinite;
}

@keyframes auth-hero-pulse {
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50% { transform: scale(1.05); opacity: 0.5; }
}

@keyframes auth-hero-orbit {
  to { transform: rotate(360deg); }
}

.auth-hero__body {
  position: relative;
  z-index: 2;
}

.auth-hero__tag {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-family: Lato, sans-serif;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #ffbe6b;
  margin-bottom: 20px;
}

.auth-hero__tag::before {
  content: "";
  width: 20px;
  height: 2px;
  background: linear-gradient(90deg, var(--brand-orange, #fc8c02), var(--brand-orange-2, #da3200));
  border-radius: 2px;
}

.auth-hero__headline {
  margin: 0 0 16px;
  font-family: "lato-black", Lato, sans-serif;
  font-size: 38px;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.03em;
  color: #fff;
  text-wrap: balance;
  max-width: 12ch;
}

.auth-hero__headline em {
  font-style: normal;
  background: linear-gradient(100deg, var(--brand-orange, #fc8c02) 10%, #ffb24d 90%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.auth-hero__desc {
  margin: 0 0 30px;
  font-family: Lato, sans-serif;
  font-size: 15px;
  line-height: 1.65;
  color: #b9c5e6;
  max-width: 42ch;
}

.auth-hero__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 13px;
}

.auth-hero__list li {
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: Lato, sans-serif;
  font-size: 14px;
  color: #dbe4fb;
}

.auth-hero__tick {
  width: 22px;
  height: 22px;
  flex: none;
  border-radius: 7px;
  display: grid;
  place-items: center;
  background: rgba(252, 140, 2, 0.16);
  color: var(--brand-orange, #fc8c02);
  font-size: 12px;
}

@media (max-width: 900px) {
  .auth-hero {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .auth-hero__orb,
  .auth-hero__dot,
  .auth-hero__ring--2 {
    animation: none;
  }
}
</style>
